import type {
  BrainTeamApprovedStorySceneLayout,
  BrainTeamApprovedStorySceneTransition,
} from '@/core/contracts/brain-team-approved-story-projection';
import {
  type BrainTeamStoryComposition,
  parseBrainTeamStoryComposition,
} from '@/core/contracts/brain-team-story-composition';
import type {
  BrainTeamStoryChapter,
  BrainTeamStoryMood,
  BrainTeamStorySceneImportance,
  BrainTeamStorySceneLinked,
  BrainTeamStorySceneMood,
  BrainTeamStorySceneSource,
} from '@/core/contracts/brain-team-story-depth';
import {
  type BrainTeamStoryExperience,
  parseStoryExperience,
} from '@/core/contracts/brain-team-story-experience';
import type { BrainTeamStoryMapName } from '@/core/contracts/brain-team-story-map-names';
import {
  type BrainTeamStoryPerformer,
  parseBrainTeamStoryPerformer,
} from '@/core/contracts/brain-team-story-performer';
import {
  type BrainTeamStoryPresentationMode,
  parseBrainTeamStoryPresentationMode,
} from '@/core/contracts/brain-team-story-presentation-mode';
import { type BrainTeamStoryTime, parseBrainTeamStoryTime } from '@/core/contracts/brain-team-story-time';
import {
  type BrainTeamStoryVisualEffect,
  parseBrainTeamStoryVisualEffect,
} from '@/core/contracts/brain-team-story-visual-effect';
import { storyObservedAtMs } from '@/core/contracts/story-observed-at';

export type BrainTeamStoryKind = 'observation_journey' | 'comparison' | 'visual_diary' | 'travel_diary';

export type BrainTeamStoryClaimType = 'observed' | 'narrated';

export interface BrainTeamStoryProjection {
  /** Evidence Stories are versioned independently from map_focus. Unknown
   * versions fail closed instead of being guessed into a visual claim. */
  schema_version: 1;
  kind: BrainTeamStoryKind;
  title: string;
  presentation_mode?: BrainTeamStoryPresentationMode;
  composition?: BrainTeamStoryComposition;
  experience?: BrainTeamStoryExperience;
  visual_effect?: BrainTeamStoryVisualEffect;
  /** How the story uses its dates. Presentation only: each scene's observed_at already honours its
   * date column. A setting this app cannot read is none. */
  time?: BrainTeamStoryTime;
  /** How the story wears its stops' moods. Carried by the Studio's preview; a generated turn never
   *  adds it. */
  mood?: BrainTeamStoryMood;
  /** The acts the scenes' `chapter` labels name. Carried from a saved story by the Studio's preview;
   *  a generated turn never adds them. */
  chapters?: BrainTeamStoryChapter[];
  /** What the story's own time called the regions its stops stand on. */
  map_names?: BrainTeamStoryMapName[];
  performer?: BrainTeamStoryPerformer;
  /** Connected points require an authorized route claim: an explicit
   * user-authored travel order, or a server-verified tracked chronology
   * ('tracked_observations' — the backend proved one tracked subject from
   * the register rows themselves before authoring it). */
  connect_points: boolean;
  path_source:
    | 'user_authored_order'
    | 'chronological_observations'
    | 'comparison_set'
    | 'tracked_observations';
  source: 'turn';
  scenes: Array<{
    target_id: string;
    sequence: number;
    label: string;
    /** The purpose attached to an authored stop ("photograph the reef"). */
    action?: string;
    /** Optional authorized narration. It remains editable in the shared composer. */
    narration?: string;
    /** PRESENTATION ONLY: a line WRITTEN for this scene when the register
     *  carried no authored narration. Deliberately separate from `narration`
     *  so a reader can always tell observed from narrated. */
    caption?: string;
    highlights?: string[];
    media_fit?: 'cover' | 'contain';
    media_zoom?: number;
    layout?: BrainTeamApprovedStorySceneLayout;
    transition?: BrainTeamApprovedStorySceneTransition;
    source_media_mode?: 'inherit' | 'hidden';
    /** Trusted document preview fields; generated turn parsing cannot add URLs. */
    source_section_keys?: string[];
    source_media_url?: string;
    source_media_fit?: 'cover' | 'contain';
    /** How long this beat holds, overriding the reading-time estimate. */
    dwell_ms?: number;
    /** The act this beat belongs to; consecutive scenes sharing one are a
     *  movement, which is what gives a long story somewhere to breathe. */
    chapter?: string;
    /** Where the subject sits in this scene's first image, 0..1 from the
     *  top-left. Absent = centre. */
    focal_point?: { x: number; y: number };
    /** How much this stop matters, what kind of stop it is, how it turned out and where to read more.
     *  Carried from a saved story by the Studio's preview; a generated turn never adds them. */
    importance?: BrainTeamStorySceneImportance;
    stop_kind?: string;
    outcome?: string;
    sources?: BrainTeamStorySceneSource[];
    mood?: BrainTeamStorySceneMood;
    /** What the stop's row is joined to in the team's other records (a story told from several
     *  files), carried from a saved story by the Studio's preview. */
    linked?: BrainTeamStorySceneLinked[];
    observed_at?: string;
    claim_type: BrainTeamStoryClaimType;
    provenance: Array<{
      kind: 'register_row' | 'user_request';
      ref_id: string;
      label?: string;
    }>;
    /** Opaque references only. The browser resolves renderable media through
     * its already-authorized local register projection. */
    media_refs: Array<{
      ref_id: string;
      kind: 'image' | 'video' | 'document' | 'link';
      /** Generated turn projections remain register-only. Team HQ's trusted
       * in-memory WYSIWYG projector may mark author presentation explicitly. */
      source?: 'register_row' | 'story_author';
      label?: string;
    }>;
  }>;
  caveats: string[];
}

const STORY_KINDS: BrainTeamStoryKind[] = [
  'observation_journey',
  'comparison',
  'visual_diary',
  'travel_diary',
];
const PATH_SOURCES: BrainTeamStoryProjection['path_source'][] = [
  'user_authored_order',
  'chronological_observations',
  'comparison_set',
  'tracked_observations',
];
const CLAIM_TYPES: BrainTeamStoryClaimType[] = ['observed', 'narrated'];
const PROVENANCE_KINDS: BrainTeamStoryProjection['scenes'][number]['provenance'][number]['kind'][] = [
  'register_row',
  'user_request',
];
const MEDIA_KINDS: BrainTeamStoryProjection['scenes'][number]['media_refs'][number]['kind'][] = [
  'image',
  'video',
  'document',
  'link',
];
const CANONICAL_REGISTER_TARGET = /^reg:[A-Za-z0-9][A-Za-z0-9._-]{0,95}:[A-Za-z0-9][A-Za-z0-9._-]{0,95}$/;
const boundedOptional = (value: unknown, max: number): string | undefined =>
  typeof value === 'string' && value.trim() && value.trim().length <= max ? value.trim() : undefined;

const isOverlong = (value: unknown, max: number): boolean =>
  typeof value === 'string' && value.trim().length > max;

// The shared chronology grammar: impossible days (2026-02-30) are refused
// rather than silently changing the evidence chronology, and a day before
// year 1 ("-000752-04-21", 753 BC) orders by time.
const observedTime = (value: string | undefined): number | undefined => storyObservedAtMs(value) ?? undefined;

/** Asset history materializes optional object fields as `null`. Treat that as
 * absent so genuine legacy map stories still survive refresh, while any
 * non-null value owns the turn even when the strict parser rejects it. */
export const hasBrainTeamStoryProjectionDirective = (value: unknown): boolean =>
  value !== null && value !== undefined;

/** Parse the bounded v1 semantic story command. This does not authorize a
 * target or resolve a media URL; Atlas performs both against its current
 * durable register projection before rendering. */
/**
 * Where the subject actually sits in a photograph, 0..1 from the top-left.
 *
 * Field evidence is wildly heterogeneous — phone portraits, drone landscapes,
 * scanned documents — so a renderer with no focal point centre-crops
 * everything and the seedling, the damaged roof or the moored vessel routinely
 * lands outside the visible frame. Absent means centre, which is what every
 * renderer did before this existed.
 */
function readFocalPoint(value: unknown): { x: number; y: number } | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const point = value as Record<string, unknown>;
  const inRange = (candidate: unknown): candidate is number =>
    typeof candidate === 'number' && Number.isFinite(candidate) && candidate >= 0 && candidate <= 1;
  if (!inRange(point.x) || !inRange(point.y)) return undefined;
  return { x: point.x, y: point.y };
}

export function parseBrainTeamStoryProjection(value: unknown): BrainTeamStoryProjection | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw = value as Record<string, unknown>;
  if (raw.schema_version !== 1 || raw.source !== 'turn') return undefined;
  // `status`/`reason` belong to the non-playable unavailable tombstone. A
  // hybrid payload must not smuggle playable scenes alongside that directive.
  if ('status' in raw || 'reason' in raw) return undefined;
  // Identity-backed tracking and quantified-change claims are intentionally
  // outside v1. Reject their legacy identity fields rather than weakening the
  // payload into an observation story.
  if ('entity_id' in raw || 'track_id' in raw) return undefined;

  const kind = STORY_KINDS.includes(raw.kind as BrainTeamStoryKind)
    ? (raw.kind as BrainTeamStoryKind)
    : undefined;
  const title = boundedOptional(raw.title, 160);
  const presentationMode = parseBrainTeamStoryPresentationMode(raw.presentation_mode);
  const pathSource = PATH_SOURCES.includes(raw.path_source as BrainTeamStoryProjection['path_source'])
    ? (raw.path_source as BrainTeamStoryProjection['path_source'])
    : undefined;
  const visualEffect = parseBrainTeamStoryVisualEffect(raw.visual_effect);
  const time = parseBrainTeamStoryTime(raw.time);
  const performer = parseBrainTeamStoryPerformer(raw.performer);
  if (
    !kind ||
    !title ||
    (raw.presentation_mode !== undefined && !presentationMode) ||
    !pathSource ||
    (raw.visual_effect !== undefined && raw.visual_effect !== null && !visualEffect) ||
    (raw.performer !== undefined && !performer)
  ) {
    return undefined;
  }
  if (!Array.isArray(raw.scenes) || raw.scenes.length < 2 || raw.scenes.length > 24) return undefined;

  const scenes = raw.scenes.map((candidate) => {
    if (!candidate || typeof candidate !== 'object') return null;
    const scene = candidate as Record<string, unknown>;
    const targetId = boundedOptional(scene.target_id, 192) ?? '';
    const sequence =
      typeof scene.sequence === 'number' && Number.isInteger(scene.sequence) && scene.sequence >= 1
        ? scene.sequence
        : 0;
    const label = boundedOptional(scene.label, 160) ?? '';
    const claimType = CLAIM_TYPES.includes(scene.claim_type as BrainTeamStoryClaimType)
      ? (scene.claim_type as BrainTeamStoryClaimType)
      : undefined;
    if (!targetId || !CANONICAL_REGISTER_TARGET.test(targetId) || !sequence || !label || !claimType) {
      return null;
    }
    if (
      isOverlong(scene.action, 240) ||
      isOverlong(scene.narration, 480) ||
      isOverlong(scene.observed_at, 64)
    ) {
      return null;
    }
    const observedAt = boundedOptional(scene.observed_at, 64);
    if ('observed_at' in scene && observedTime(observedAt) === undefined) return null;
    if (!Array.isArray(scene.provenance) || !Array.isArray(scene.media_refs)) return null;

    const provenance = scene.provenance.map((candidateProvenance) => {
      if (!candidateProvenance || typeof candidateProvenance !== 'object') return null;
      const item = candidateProvenance as Record<string, unknown>;
      const kind = PROVENANCE_KINDS.includes(
        item.kind as BrainTeamStoryProjection['scenes'][number]['provenance'][number]['kind'],
      )
        ? (item.kind as BrainTeamStoryProjection['scenes'][number]['provenance'][number]['kind'])
        : undefined;
      const refId = boundedOptional(item.ref_id, 192) ?? '';
      if (!kind || !refId || isOverlong(item.label, 160)) return null;
      return {
        kind,
        ref_id: refId,
        ...(boundedOptional(item.label, 160) ? { label: boundedOptional(item.label, 160) } : {}),
      };
    });
    if (provenance.some((item) => !item) || provenance.length > 12) return null;
    const validProvenance = provenance as BrainTeamStoryProjection['scenes'][number]['provenance'];
    const provenanceKeys = new Set(validProvenance.map((item) => `${item.kind}:${item.ref_id}`));
    const registerRowRefs = validProvenance.filter((item) => item.kind === 'register_row');
    if (
      provenanceKeys.size !== validProvenance.length ||
      registerRowRefs.length !== 1 ||
      registerRowRefs[0]?.ref_id !== targetId
    ) {
      return null;
    }

    const mediaRefs = scene.media_refs.map((candidateMedia) => {
      if (!candidateMedia || typeof candidateMedia !== 'object') return null;
      const item = candidateMedia as Record<string, unknown>;
      const kind = MEDIA_KINDS.includes(
        item.kind as BrainTeamStoryProjection['scenes'][number]['media_refs'][number]['kind'],
      )
        ? (item.kind as BrainTeamStoryProjection['scenes'][number]['media_refs'][number]['kind'])
        : undefined;
      const refId = boundedOptional(item.ref_id, 192) ?? '';
      // Untrusted model output can cite authorized register media only. The
      // story_author discriminant is reserved for the local HQ preview and the
      // separately parsed approved-library contract.
      if (
        !kind ||
        !refId ||
        (item.source !== undefined && item.source !== 'register_row') ||
        isOverlong(item.label, 160)
      )
        return null;
      return {
        ref_id: refId,
        kind,
        ...(boundedOptional(item.label, 160) ? { label: boundedOptional(item.label, 160) } : {}),
      };
    });
    if (mediaRefs.some((item) => !item) || mediaRefs.length > 8) return null;

    const caption = boundedOptional(scene.caption, 480);
    const chapter = boundedOptional(scene.chapter, 80);
    const dwellMs =
      typeof scene.dwell_ms === 'number' &&
      Number.isSafeInteger(scene.dwell_ms) &&
      scene.dwell_ms >= 800 &&
      scene.dwell_ms <= 20000
        ? scene.dwell_ms
        : undefined;
    const focalPoint = readFocalPoint(scene.focal_point);
    const mediaFit =
      scene.media_fit === 'cover' || scene.media_fit === 'contain' ? scene.media_fit : undefined;
    const mediaZoom =
      typeof scene.media_zoom === 'number' &&
      Number.isFinite(scene.media_zoom) &&
      scene.media_zoom >= 1 &&
      scene.media_zoom <= 2
        ? scene.media_zoom
        : undefined;
    const layout =
      typeof scene.layout === 'string' &&
      ['split', 'cinematic', 'editorial', 'grid', 'statement'].includes(scene.layout)
        ? (scene.layout as BrainTeamApprovedStorySceneLayout)
        : undefined;
    const transition =
      typeof scene.transition === 'string' && ['fade', 'slide', 'zoom', 'none'].includes(scene.transition)
        ? (scene.transition as BrainTeamApprovedStorySceneTransition)
        : undefined;
    const sourceMediaMode =
      scene.source_media_mode === 'inherit' || scene.source_media_mode === 'hidden'
        ? scene.source_media_mode
        : undefined;
    const highlights = Array.isArray(scene.highlights) ? scene.highlights : undefined;
    if (
      (scene.media_fit !== undefined && !mediaFit) ||
      (scene.media_zoom !== undefined && mediaZoom === undefined) ||
      (scene.layout !== undefined && !layout) ||
      (scene.transition !== undefined && !transition) ||
      (scene.source_media_mode !== undefined && !sourceMediaMode) ||
      scene.source_media_url !== undefined ||
      (scene.highlights !== undefined &&
        (!highlights ||
          highlights.length > 3 ||
          new Set(highlights).size !== highlights.length ||
          highlights.some(
            (value) =>
              typeof value !== 'string' || !value.trim() || value !== value.trim() || value.length > 80,
          )))
    )
      return null;
    return {
      target_id: targetId,
      sequence,
      label,
      ...(boundedOptional(scene.action, 240) ? { action: boundedOptional(scene.action, 240) } : {}),
      ...(boundedOptional(scene.narration, 480) ? { narration: boundedOptional(scene.narration, 480) } : {}),
      // PERFORMANCE AND PRESENTATION — see the approved-library parser for why
      // these four have to be named here or they are silently discarded.
      ...(caption ? { caption } : {}),
      ...(highlights ? { highlights: [...highlights] as string[] } : {}),
      ...(mediaFit ? { media_fit: mediaFit } : {}),
      ...(mediaZoom !== undefined ? { media_zoom: mediaZoom } : {}),
      ...(layout ? { layout } : {}),
      ...(transition ? { transition } : {}),
      ...(sourceMediaMode ? { source_media_mode: sourceMediaMode } : {}),
      ...(dwellMs !== undefined ? { dwell_ms: dwellMs } : {}),
      ...(chapter ? { chapter } : {}),
      ...(focalPoint ? { focal_point: focalPoint } : {}),
      ...(observedAt ? { observed_at: observedAt } : {}),
      claim_type: claimType,
      provenance: validProvenance,
      media_refs: mediaRefs as BrainTeamStoryProjection['scenes'][number]['media_refs'],
    };
  });
  if (scenes.some((scene) => !scene)) return undefined;
  const validScenes = scenes as BrainTeamStoryProjection['scenes'];
  const composition = parseBrainTeamStoryComposition(
    raw.composition,
    validScenes.map((scene) => scene.target_id),
  );
  if (raw.composition !== undefined && !composition) return undefined;
  const experience = parseStoryExperience(
    raw.experience,
    validScenes.map((scene) => scene.target_id),
  );
  if (raw.experience !== undefined && !experience) return undefined;
  const targetIds = new Set(validScenes.map((scene) => scene.target_id));
  const hasIntegrity =
    targetIds.size === validScenes.length &&
    validScenes.every((scene, index) => scene.sequence === index + 1) &&
    validScenes.every((scene) => {
      const registerRowRefs = scene.provenance.filter((item) => item.kind === 'register_row');
      return registerRowRefs.length === 1 && registerRowRefs[0]?.ref_id === scene.target_id;
    });
  if (!hasIntegrity) return undefined;

  const everySceneHas = (
    scene: BrainTeamStoryProjection['scenes'][number],
    provenanceKind: BrainTeamStoryProjection['scenes'][number]['provenance'][number]['kind'],
  ) => scene.provenance.some((item) => item.kind === provenanceKind);
  const chronologicalStory = kind === 'observation_journey' || kind === 'visual_diary';
  const observedTimes = validScenes.map((scene) => observedTime(scene.observed_at));
  const hasOrderedObservations =
    !chronologicalStory ||
    (observedTimes.every((time) => time !== undefined) &&
      observedTimes.every((time, index) => index === 0 || time! >= observedTimes[index - 1]!));
  if (!hasOrderedObservations) return undefined;

  // The one connected chronology: a tracked observation journey, authored
  // only when the server verified a single tracked subject from the rows.
  const trackedJourney =
    kind === 'observation_journey' && raw.connect_points === true && pathSource === 'tracked_observations';
  const validShape =
    kind === 'travel_diary'
      ? raw.connect_points === true &&
        pathSource === 'user_authored_order' &&
        validScenes.every((scene) => {
          const userRequestRefs = scene.provenance.filter((item) => item.kind === 'user_request');
          return (
            scene.claim_type === 'narrated' &&
            everySceneHas(scene, 'register_row') &&
            userRequestRefs.length === 1 &&
            userRequestRefs[0]?.ref_id === 'current_turn'
          );
        })
      : (trackedJourney ||
          (raw.connect_points === false &&
            (kind === 'comparison'
              ? pathSource === 'comparison_set'
              : pathSource === 'chronological_observations'))) &&
        validScenes.every(
          (scene) =>
            scene.claim_type === 'observed' &&
            everySceneHas(scene, 'register_row') &&
            scene.provenance.every((item) => item.kind === 'register_row') &&
            (kind !== 'visual_diary' || scene.media_refs.length > 0),
        );
  if (!validShape) return undefined;

  if (!Array.isArray(raw.caveats) || raw.caveats.length > 8) return undefined;
  const caveats = raw.caveats.map((item) => boundedOptional(item, 240));
  if (caveats.some((item) => item === undefined)) return undefined;
  return {
    schema_version: 1,
    kind,
    title,
    ...(presentationMode ? { presentation_mode: presentationMode } : {}),
    ...(composition ? { composition } : {}),
    ...(experience ? { experience } : {}),
    ...(visualEffect ? { visual_effect: visualEffect } : {}),
    ...(time ? { time } : {}),
    ...(performer ? { performer } : {}),
    connect_points: raw.connect_points === true,
    path_source: pathSource,
    source: 'turn',
    scenes: validScenes,
    caveats: caveats as string[],
  };
}
